import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { isEmpty } from 'lodash';
import moment from 'moment';

import { ServicingSection, FlexRow, FlexColumn } from 'web-ui-components/lib/global/layout';
import { getSelectedAccount } from '../../../states/accounts/selectors';

import PaymentDetailsHeader from './PaymentDetailsHeader/PaymentDetailsHeader';
import WhoPaysSection from './WhoPaysSection/WhoPaysSection';
import PaymentsDateSelector from './PaymentsDateSelector/PaymentsDateSelector';
import HowToSendSection from './HowToSendSection/HowToSendSection';
import PaymentsReasonSection from './PaymentsReasonSection/PaymentsReasonSection';
import CurrencySelector from './CurrencySelector/CurrencySelector';
import RateBox from './RateBox/RateBox';
import {
	getIFTSelectedPayment,
	getCurrencyList,
	getIFTCalendarType,
	getFromCurrency,
	isBeneficiaryFromEEACountries,
	isFromSameCurrency,
	showDefaultCurrency,
	isExoticCurrency,
	getIsEditing,
	isPreBookedRateMandatory,
	getReasonCodeList,
	checkCountryCodeForExoticCurrency,
	getRateInformation,
	getPaymentCount,
	getIsPaymentListEdit,
} from '../../../states/ift/selectors';
import {
	iftUpdateSelectedPaymentDetails,
	iftUpdatePayment,
	iftClearSelectedPayment,
	iftUpdatePreBookedChecked,
	iftUpdateSendAmountCurrency,
	iftDeletePayment,
} from '../../../states/ift/actions';
import {
	isFromAccountExoticCurrency,
	isPreBookedMandatory,
	getFilteredSubmissionDates,
	cleanSwiftFormatCharacters,
	isUrgentPayment,
} from '../../../helpers/fieldHelpers';

import { getInternationalPayees } from '../../../states/payees/selectors';

import { fetchCalendar } from '../../../states/calendar/actions';
import { getSubmissionDates } from '../../../states/calendar/selectors';
import PreBookedCheckBox from './PreBookedCheckBox/PreBookedCheckBox';

import FromToCurrencySection from './FromToCurrencySection/FromToCurrencySection';
import NewPaymentDatesModal from './NewPaymentDatesModal/NewPaymentDatesModal';
import { goToPreviousPage, goToPage, pages } from '../../../states/page/actions';
import ContinueFooterBar from '../../../sharedComponents/ContinueFooterBar/ContinueFooterBar';
import { initValidationIFTPaymentDetails, updateValidation, removeKey } from '../../../states/validation/actions';
import { getValidationsIFTPaymentDetails, isIFTPaymentDetailsFormValid } from '../../../states/validation/selectors';
import { iftFormValidationLength, WHO_PAYS_TYPE_SHARED } from '../../../config/ift';
import PaymentLimitsModal from '../../../fasterPayments/components/Payments/PaymentLimitsModal';
import { getAccountsWithIFTCapability } from '../../../states/accounts/selectors';
import { fromAccountPropType } from '../../../states/accounts/propTypes';
import { swiftMultiChar } from '../../../containers/withValidation';

const mapState = (state) => ({
	payment: getIFTSelectedPayment(state),
	calendarType: getIFTCalendarType(state),
	submissionDates: getSubmissionDates(state),
	currencyList: getCurrencyList(state),
	fromCurrency: getFromCurrency(state),
	isBeneficiaryEEACountry: isBeneficiaryFromEEACountries(state),
	validations: getValidationsIFTPaymentDetails(state),
	isFormValid: isIFTPaymentDetailsFormValid(state),
	isFromSameCurrency: isFromSameCurrency(state),
	showDefaultCurrency: showDefaultCurrency(state),
	isExoticCurrency: isExoticCurrency(state),
	isEditing: getIsEditing(state),
	isPreBookedRateMandatory: isPreBookedRateMandatory(state),
	reasonCodeList: getReasonCodeList(state),
	countryCode: checkCountryCodeForExoticCurrency(state),
	rates: getRateInformation(state),
	paymentCount: getPaymentCount(state),
	isPaymentListEdit: getIsPaymentListEdit(state),
	fromAccounts: getAccountsWithIFTCapability(state),
	toPayees: getInternationalPayees(state),
});

const mapDispatch = (dispatch) => ({
	handleGoToPreviousPage: () => dispatch(goToPreviousPage()),
	handleContinue: (payment) => {
		dispatch(iftUpdatePayment(payment));
		dispatch(iftClearSelectedPayment());
		dispatch(goToPage(pages.IFTPayments));
	},
	fetchCalendar: (calendarType, urgent) => dispatch(fetchCalendar(calendarType, urgent, true)),
	handleUpdateSelectedPaymentDetails: (key, value) => dispatch(iftUpdateSelectedPaymentDetails(key, value)),
	handleUpdatePreBookedChecked: (isChecked) => dispatch(iftUpdatePreBookedChecked(isChecked)),
	initValidation: () => dispatch(initValidationIFTPaymentDetails()),
	removeValidation: (id) => dispatch(removeKey(id)),
	updateValidation: (key, isValid) =>
		dispatch(
			updateValidation({
				id: 'iftPaymentDetails',
				key,
				isValid,
			})
		),
	handleSendAmountCurrencySelect: (isSendAmountFromDebitCurrency) =>
		dispatch(iftUpdateSendAmountCurrency(isSendAmountFromDebitCurrency)),
	handleCurrencySelection: (selectedCurrency, isPreBookedMandatory) => {
		dispatch(iftUpdateSelectedPaymentDetails('sendCurrency', selectedCurrency));
		dispatch(iftUpdatePreBookedChecked(isPreBookedMandatory));
		dispatch(iftUpdateSelectedPaymentDetails('preBookedReference', ''));
		dispatch(iftUpdateSelectedPaymentDetails('exchangeRate', ''));
		dispatch(iftUpdateSelectedPaymentDetails('chargingModal', WHO_PAYS_TYPE_SHARED)); // defualting to Shared charging type to prevent the wrong type when the other type was selected first before changing the currency to RON, when the 'who pays section' is disabled
	},
	handleUpdatePayment: (payment) => dispatch(iftUpdatePayment(payment)),
	goToLandingPage: () => dispatch(goToPage(pages.IFTLandingPage)),
	goToPaymentList: () => dispatch(goToPage(pages.IFTPayments)),
	deletePayment: (id) => dispatch(iftDeletePayment(id)),
});

export class IFTPaymentDetails extends Component {
	constructor(props) {
		super(props);
		this.handleContinue = this.handleContinue.bind(this);
		this.handleClose = this.handleClose.bind(this);
		this.handleCurrencySelection = this.handleCurrencySelection.bind(this);
		this.updateRefreshRequired = this.updateRefreshRequired.bind(this);
		this.handleUpdateNewPaymentDate = this.handleUpdateNewPaymentDate.bind(this);
		this.onFreshRateReceived = this.onFreshRateReceived.bind(this);

		this.state = {
			rateNeedsRefreshed: true,
			newPaymentDateAcknowledged: false,
			isBICCodeInvalid: false,
			reasonIsInvalid: false,
		};
	}
	componentWillMount() {
		const {
			initValidation,
			toPayees,
			handleUpdateSelectedPaymentDetails,
			payment: { toAccount },
		} = this.props;

		const selectedAccount = getSelectedAccount([], toPayees, toAccount) || {};
		const { bic = '' } = selectedAccount;
		const isValidBIC = !bic.endsWith('1');

		this.setState({ isValidBIC });

		if (!isValidBIC) {
			handleUpdateSelectedPaymentDetails('urgent', false);
		}

		initValidation();
	}
	componentDidMount() {
		const {
			calendarType,
			fetchCalendar,
			payment: { urgent },
		} = this.props;
		fetchCalendar(calendarType, urgent);
	}
	componentDidUpdate(prevProps) {
		const {
			calendarType,
			fetchCalendar,
			payment: { urgent },
		} = this.props;
		if (prevProps.calendarType !== calendarType || prevProps.payment.urgent !== urgent) {
			fetchCalendar(calendarType, urgent);
		}
	}
	componentWillUnmount() {
		const { removeValidation } = this.props;
		removeValidation({ id: 'iftPaymentDetails' });
	}

	onFreshRateReceived() {
		this.setState({
			newPaymentDateAcknowledged: false,
		});
	}

	handleCurrencySelection(selectedCurrency) {
		const { handleCurrencySelection, fromCurrency, isBeneficiaryEEACountry, handleUpdateSelectedPaymentDetails } =
			this.props;
		if (isBeneficiaryEEACountry) {
			handleUpdateSelectedPaymentDetails('urgent', true);
		}
		handleCurrencySelection(selectedCurrency, isPreBookedMandatory(fromCurrency, selectedCurrency));
	}

	handleUpdateNewPaymentDate() {
		const { handleUpdateSelectedPaymentDetails, rates } = this.props;
		handleUpdateSelectedPaymentDetails('date', moment(rates.settlementDate));
		this.setState({
			newPaymentDateAcknowledged: true,
		});
	}

	handleClose() {
		const {
			goToLandingPage,
			isEditing,
			handleBackParent,
			paymentCount,
			goToPaymentList,
			payment,
			deletePayment,
			isPaymentListEdit,
		} = this.props;

		if (isEditing) return handleBackParent();
		if (isPaymentListEdit) return goToPaymentList();
		if (paymentCount > 1) {
			deletePayment(payment.id);
			return goToPaymentList();
		}
		return goToLandingPage();
	}

	handleContinue() {
		const { handleContinue, payment, isEditing, onEditContinue, handleUpdatePayment } = this.props;

		const paymentReason = payment.reason;

		if (paymentReason && swiftMultiChar(paymentReason) === false) {
			this.setState({
				reasonIsInvalid: !swiftMultiChar(paymentReason),
			});
		} else if (isEditing) {
			handleUpdatePayment(payment);
			onEditContinue();
		} else {
			handleContinue(payment);
		}
	}

	updateRefreshRequired(rateNeedsRefreshed) {
		this.setState({
			rateNeedsRefreshed,
		});
	}

	render() {
		const { rateNeedsRefreshed, newPaymentDateAcknowledged, isValidBIC, reasonIsInvalid } = this.state;
		const {
			payment: {
				date,
				sendAmount,
				urgent,
				reason,
				chargingModal,
				sendCurrency,
				fromAccount,
				toAccount,
				isPreBookedRate,
				preBookedReference,
				exchangeRate,
				isSendAmountFromDebitCurrency,
				isDefaultCurrency,
			},
			submissionDates,
			handleUpdateSelectedPaymentDetails,
			currencyList,
			fromCurrency,
			handleSendAmountCurrencySelect,
			isBeneficiaryEEACountry,
			isFormValid,
			handleUpdatePreBookedChecked,
			validations,
			updateValidation,
			isFromSameCurrency,
			showDefaultCurrency,
			isExoticCurrency,
			isPreBookedRateMandatory,
			reasonCodeList,
			countryCode,
			rates,
			isEditing,
			fromAccounts,
			toPayees,
		} = this.props;
		const creditAmount = !isSendAmountFromDebitCurrency ? sendAmount : rates.creditAmount;
		const debitAmount = isSendAmountFromDebitCurrency ? sendAmount : rates.debitAmount;
		const validationProps = { validations, updateValidation };
		const showContinueButton = isFormValid && !isEmpty(rates) && !isEmpty(reason) && !rateNeedsRefreshed;
		const filteredSubmissionDates = getFilteredSubmissionDates(submissionDates, isPreBookedRate);
		const isUrgentDisabled = isUrgentPayment(isBeneficiaryEEACountry, sendCurrency) || !isValidBIC;
		const isWhoPaySectionDisabled = (isBeneficiaryEEACountry && !urgent) || sendCurrency === 'RON';
		return (
			<div>
				<FromToCurrencySection
					fromAccount={fromAccount}
					toAccount={toAccount}
					sendCurrency={sendCurrency}
					handleClose={this.handleClose}
					creditAmount={creditAmount}
					fromAccounts={fromAccounts}
					toPayees={toPayees}
					isPaymentFlow
				/>
				<ServicingSection>
					<FlexRow>
						<FlexColumn>
							<PaymentDetailsHeader isEditing={isEditing} />
							<CurrencySelector
								currencyList={currencyList}
								selectedCurrency={sendCurrency}
								onCurrencySelection={this.handleCurrencySelection}
								showDefaultCurrency={showDefaultCurrency}
								isDefaultCurrency={isDefaultCurrency}
								onSettingDefaultCurrency={({ target: { checked } }) =>
									handleUpdateSelectedPaymentDetails('isDefaultCurrency', checked)
								}
								isFromAccountExoticCurrency={isFromAccountExoticCurrency(fromCurrency)}
							/>
							{!isFromSameCurrency && (
								<PreBookedCheckBox
									{...validationProps}
									onPreBookedSelection={(event) => handleUpdatePreBookedChecked(event.target.checked)}
									isPreBookedRate={isPreBookedRate}
									isPreBookedRateMandatory={isPreBookedRateMandatory}
									preBookedReference={preBookedReference}
									exchangeRate={exchangeRate}
									handleReferenceUpdate={(preBookedReference) =>
										handleUpdateSelectedPaymentDetails('preBookedReference', preBookedReference)
									}
									handleRateUpdate={(exchangeRate) => handleUpdateSelectedPaymentDetails('exchangeRate', exchangeRate)}
								/>
							)}
							<PaymentsDateSelector
								{...validationProps}
								paymentDates={filteredSubmissionDates}
								onChange={(date) => {
									handleUpdateSelectedPaymentDetails('date', date);
								}}
								onCheckboxChange={(event) => {
									handleUpdateSelectedPaymentDetails('urgent', event.target.checked);
									if (isBeneficiaryEEACountry && !event.target.checked) {
										handleUpdateSelectedPaymentDetails('chargingModal', WHO_PAYS_TYPE_SHARED);
									}
								}}
								isUrgentPaymentChecked={urgent}
								date={date}
								disabled={isUrgentDisabled}
							/>
							<HowToSendSection
								{...validationProps}
								onChange={(amount) => {
									handleUpdateSelectedPaymentDetails('sendAmount', amount);
								}}
								onCurrencyRadioSelect={handleSendAmountCurrencySelect}
								debitAmount={debitAmount}
								creditAmount={creditAmount}
								sendCurrency={sendCurrency}
								fromCurrency={fromCurrency}
								sendAmount={sendAmount}
								isSendAmountFromDebitCurrency={isSendAmountFromDebitCurrency}
								isExoticCurrency={isExoticCurrency}
							/>
							<WhoPaysSection
								onChange={(event) => handleUpdateSelectedPaymentDetails('chargingModal', event.target.id)}
								disabled={isWhoPaySectionDisabled}
								selectedId={isWhoPaySectionDisabled ? WHO_PAYS_TYPE_SHARED : chargingModal}
							/>
							<PaymentsReasonSection
								{...validationProps}
								onChange={({ value = '' }) => {
									handleUpdateSelectedPaymentDetails('reason', cleanSwiftFormatCharacters(value));
								}}
								maxLength={iftFormValidationLength.iftPaymentsReasonLength}
								reason={reason}
								countryCode={countryCode}
								reasonCodeList={reasonCodeList}
								reasonIsInvalid={reasonIsInvalid}
							/>
						</FlexColumn>
						<FlexColumn>
							<RateBox
								isPreBookedRateMandatory={isPreBookedRateMandatory}
								isPreBookedRate={isPreBookedRate}
								fromCurrency={fromCurrency}
								sendCurrency={sendCurrency}
								chargingModal={chargingModal}
								updateRefreshRequired={this.updateRefreshRequired}
								onFreshRateReceived={this.onFreshRateReceived}
							/>
						</FlexColumn>
						<NewPaymentDatesModal
							rates={rates}
							updateNewPaymentDates={this.handleUpdateNewPaymentDate}
							newPaymentDateAcknowledged={newPaymentDateAcknowledged}
						/>
					</FlexRow>
				</ServicingSection>
				<ContinueFooterBar showFooter={showContinueButton} onContinue={this.handleContinue} />
				<PaymentLimitsModal />
			</div>
		);
	}
}
export default compose(connect(mapState, mapDispatch))(IFTPaymentDetails);

IFTPaymentDetails.propTypes = {
	payment: PropTypes.shape({
		sendAmount: PropTypes.string.isRequired,
		urgent: PropTypes.bool.isRequired,
		reason: PropTypes.string.isRequired,
		chargingModal: PropTypes.string.isRequired,
		sendCurrency: PropTypes.string.isRequired,
		fromAccount: PropTypes.string.isRequired,
		toAccount: PropTypes.string.isRequired,
		isSendAmountFromDebitCurrency: PropTypes.bool.isRequired,
		isPreBookedRate: PropTypes.bool.isRequired,
		isDefaultCurrency: PropTypes.bool.isRequired,
	}),
	currencyList: PropTypes.arrayOf(PropTypes.shape({})),
	handleGoToPreviousPage: PropTypes.func.isRequired,
	handleContinue: PropTypes.func.isRequired,
	handleUpdateSelectedPaymentDetails: PropTypes.func.isRequired,
	fetchCalendar: PropTypes.func.isRequired,
	calendarType: PropTypes.string.isRequired,
	isBeneficiaryEEACountry: PropTypes.bool.isRequired,
	isFormValid: PropTypes.bool.isRequired,
	submissionDates: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
	initValidation: PropTypes.func.isRequired,
	handleUpdatePreBookedChecked: PropTypes.func.isRequired,
	fromCurrency: PropTypes.string.isRequired,
	handleSendAmountCurrencySelect: PropTypes.func.isRequired,
	updateValidation: PropTypes.func.isRequired,
	validations: PropTypes.shape({}).isRequired,
	isFromSameCurrency: PropTypes.bool.isRequired,
	handleCurrencySelection: PropTypes.func.isRequired,
	showDefaultCurrency: PropTypes.bool.isRequired,
	isExoticCurrency: PropTypes.bool.isRequired,
	isEditing: PropTypes.bool,
	onEditContinue: PropTypes.func,
	handleUpdatePayment: PropTypes.func,
	isPreBookedRateMandatory: PropTypes.bool,
	rates: PropTypes.shape({}),
	handleBackParent: PropTypes.func,
	reasonCodeList: PropTypes.arrayOf(PropTypes.string),
	countryCode: PropTypes.bool.isRequired,
	goToLandingPage: PropTypes.func.isRequired,
	paymentCount: PropTypes.number.isRequired,
	goToPaymentList: PropTypes.func.isRequired,
	deletePayment: PropTypes.func.isRequired,
	isPaymentListEdit: PropTypes.bool.isRequired,
	removeValidation: PropTypes.func.isRequired,
	fromAccounts: PropTypes.arrayOf(PropTypes.shape(fromAccountPropType)).isRequired,
};
